import React from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import {
  Avatar,
  Tooltip,
  Grid,
  Select,
  MenuItem,
  OutlinedInput,
  FormControl,
  InputLabel,
  AvatarGroup,
} from '@mui/material';
import { getInitials } from '../../../lib/helpers/helpers';
import { ArrowDropDown } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    padding: 0,
    margin: 0,
  },
}));

const PatientAssociatedAdminsSelect = ({ data }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const selectRef = React.useRef(null);

  const handleToggle = () => {
    setOpen(!open);
    selectRef?.current?.focus();
  };

  const renderAvatars = () => {
    const validAdmins = data.value.filter((admin) => admin.name && admin.name.trim() !== '');
    const displayedAdmins = validAdmins.slice(0, 3);
    const remainingCount = validAdmins.length - 3;

    return (
      <AvatarGroup
        max={Math.max(2, validAdmins.length > 4 ? 4 : validAdmins.length)}
        sx={{
          '& .MuiAvatar-root': {
            border: '2px solid var(--background-paper-elevation-0, #FFF)',
            bgcolor: theme.palette.admin.main,
          },
          '& .MuiAvatar-root:hover': {
            bgcolor: theme.palette.admin.main,
          },
          display: 'flex',
          alignItems: 'center',
          height: '32px',
        }}
      >
        {displayedAdmins.map((admin) => (
          <Tooltip
            title={admin.name}
            key={admin.id}
          >
            <Avatar
              sx={{
                borderRadius: '100px',
                margin: 0,
                verticalAlign: 'middle',
              }}
            >
              {getInitials(admin.name)}
            </Avatar>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip title={`+${remainingCount} more`}>
            <Avatar
              sx={{
                borderRadius: '100px',
                margin: 0,
                verticalAlign: 'middle',
              }}
            >
              +{remainingCount}
            </Avatar>
          </Tooltip>
        )}
      </AvatarGroup>
    );
  };

  const validAdmins = data?.value?.filter((admin) => admin.name && admin.name.trim() !== '') || [];

  if (!validAdmins.length) {
    return null; // Hide the component if there are no valid admins
  }

  return (
    <Grid
      item
      xs={12}
      className={classes.patientChartCard}
    >
      <FormControl
        variant="outlined"
        fullWidth
        onClick={handleToggle}
        sx={{
          cursor: 'pointer',
          minWidth: '232px',
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.admin.main,
              borderWidth: '2px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.admin.main,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
            },
          },
        }}
      >
        <InputLabel
          id="associated-admins-label"
          shrink={open || data.value.length > 0}
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          sx={{
            color: open ? theme.palette.admin.main : theme.palette.admin.backgroundHover,
            '&.Mui-focused': {
              color: theme.palette.admin.main,
            },
            cursor: 'pointer',
          }}
        >
          Associated Admins
        </InputLabel>
        <Select
          ref={selectRef}
          labelId="associated-admins-label"
          open={open}
          onClose={handleToggle}
          onOpen={handleToggle}
          displayEmpty
          onClick={(e) => e.stopPropagation()}
          MenuProps={{
            disableAutoFocusItem: true,
            autoFocus: false,
            disableAutoFocus: true,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            sx: {
              '& .MuiMenuItem-root': {
                cursor: 'default',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent',
                },
                margin: 0,
              },
            },
          }}
          value=""
          input={
            <OutlinedInput
              label="Associated Admins"
              notched
            />
          }
          renderValue={() => renderAvatars()}
          IconComponent={() => (
            <ArrowDropDown
              sx={{
                width: 24,
                height: 24,
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                pointerEvents: 'none',
                color: 'rgba(0, 0, 0, 0.56)',
              }}
            />
          )}
          sx={{
            height: '64px',
            '& .MuiSelect-icon': {
              right: 12,
              transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
              pointerEvents: 'none',
            },
            cursor: 'pointer',
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              padding: '8px 32px 8px 14px',
              height: '48px',
              boxSizing: 'border-box',
            },
          }}
        >
          {validAdmins.map((admin) => (
            <MenuItem
              key={admin.id}
              value={admin.id}
              onClick={(e) => e.stopPropagation()}
              disableRipple
              disabled={false}
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.admin.main,
                  marginRight: 1,
                }}
              >
                {getInitials(admin.name)}
              </Avatar>
              {admin.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );
};

export default PatientAssociatedAdminsSelect;
